import { Controller } from '@hotwired/stimulus';

/* 
  https://github.com/afcapel/stimulus-autocomplete
  필요한 부분만 가져와서 사용함
*/
const optionSelector = "[role='option']:not([aria-disabled])";
const activeSelector = "[aria-selected='true']";
const resultsClassname = 'autocomplete-list-group';
const itemClassname = 'autocomplete-list-group-item';

export default class extends Controller {
  static targets = ['input', 'results', 'list']; // results = 검색 결과, list = 검색어 목록
  static classes = ['selected'];
  static list; // listTarget을 배열로 사용

  connect() {
    this.close();
    this.list = [].slice.call(this.listTarget.children);
    this.list.map((x) => {
      x.className = itemClassname;
      x.role = 'option';
    });
    this.resultsTarget.className = `${resultsClassname} ${this.resultsTarget.className}`;

    this.inputTarget.setAttribute('autocomplete', 'off');
    this.inputTarget.setAttribute('spellcheck', 'false');
    this.inputTarget.addEventListener('keydown', this.onKeydown);
    this.inputTarget.addEventListener('blur', this.onInputBlur);
    this.inputTarget.addEventListener('input', this.onInputChange);
    this.resultsTarget.addEventListener('mousedown', this.onResultsMouseDown);
    this.resultsTarget.addEventListener('click', this.onResultsClick);
  }

  disconnect() {
    this.inputTarget.removeEventListener('keydown', this.onKeydown);
    this.inputTarget.removeEventListener('blur', this.onInputBlur);
    this.inputTarget.removeEventListener('input', this.onInputChange);
    this.resultsTarget.removeEventListener('mousedown', this.onResultsMouseDown);
    this.resultsTarget.removeEventListener('click', this.onResultsClick);
  }

  sibling(next) {
    const options = this.options;
    const selected = this.selectedOption;
    const index = options.indexOf(selected);
    const sibling = next ? options[index + 1] : options[index - 1];
    const def = next ? options[0] : options[options.length - 1];
    return sibling || def;
  }

  select(target) {
    const previouslySelected = this.selectedOption;
    if (previouslySelected) {
      previouslySelected.removeAttribute('aria-selected');
      previouslySelected.classList.remove(...this.selectedClassesOrDefault);
    }

    target.setAttribute('aria-selected', 'true');
    target.classList.add(...this.selectedClassesOrDefault);
    this.inputTarget.setAttribute('aria-activedescendant', target.id);
    target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  onKeydown = (event) => {
    const handler = this[`on${event.key}Keydown`];
    if (handler) handler(event);
  };

  onArrowDownKeydown = (event) => {
    const item = this.sibling(true);
    if (item) this.select(item);
    event.preventDefault();
  };

  onArrowUpKeydown = (event) => {
    const item = this.sibling(false);
    if (item) this.select(item);
    event.preventDefault();
  };

  onEnterKeydown = (event) => {
    const selected = this.selectedOption;
    if (selected && this.resultsShown) {
      this.commit(selected);
      event.preventDefault();
    }
  };

  onInputBlur = () => {
    this.close();
  };

  commit(selected) {
    if (selected.getAttribute('aria-disabled') === 'true') return;

    if (selected instanceof HTMLAnchorElement) {
      console.log('select click');
      selected.click();
      this.close();
      return;
    }

    const value = selected.textContent.trim();
    this.inputTarget.value = value;

    this.inputTarget.focus();
    this.hideAndRemoveOptions();

    this.element.dispatchEvent(
      new CustomEvent('autocomplete.change', {
        bubbles: true,
        detail: { value: value, selected: selected },
      }),
    );

    // option 선택시 location.href 이외의 다른 동작이 필요한경우 조건을 통해 상황에 대처할 수 있도록 수정한다.
    location.href = selected.dataset.location;
  }

  onResultsClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const selected = event.target.closest(optionSelector);
    console.log(`mouse event ${selected.dataset.location}`);
    if (selected) this.commit(selected);
  };

  onResultsMouseDown = (event) => {
    event.preventDefault();
  };

  onInputChange = () => {
    const keyword = this.inputTarget.value.trim();
    if (keyword) {
      this.replaceResults(
        this.list
          .filter((li) => li.innerHTML.includes(keyword))
          .map((li) => li.outerHTML)
          .join(''),
      );
    } else {
      this.hideAndRemoveOptions();
    }
  };

  hideAndRemoveOptions() {
    this.close();
    this.resultsTarget.innerHTML = null;
  }

  replaceResults(html) {
    this.resultsTarget.innerHTML = html;
    if (!!this.options) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    if (this.resultsShown) return;

    this.resultsShown = true;
    this.element.setAttribute('aria-expanded', 'true');
    this.element.dispatchEvent(
      new CustomEvent('toggle', {
        detail: { action: 'open', inputTarget: this.inputTarget, resultsTarget: this.resultsTarget },
      }),
    );
  }

  close() {
    if (!this.resultsShown) return;

    this.resultsShown = false;
    this.inputTarget.removeAttribute('aria-activedescendant');
    this.element.setAttribute('aria-expanded', 'false');
    this.element.dispatchEvent(
      new CustomEvent('toggle', {
        detail: { action: 'close', inputTarget: this.inputTarget, resultsTarget: this.resultsTarget },
      }),
    );
  }

  get resultsShown() {
    return !this.resultsTarget.hidden;
  }

  set resultsShown(value) {
    this.resultsTarget.hidden = !value;
  }

  get options() {
    return Array.from(this.resultsTarget.querySelectorAll(optionSelector));
  }

  get selectedOption() {
    return this.resultsTarget.querySelector(activeSelector);
  }

  get selectedClassesOrDefault() {
    return this.hasSelectedClass ? this.selectedClasses : ['active'];
  }
}
